import React from 'react';
import {
  X,
  AlertTriangle,
  Car,
  User,
  Calendar,
  Euro,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  FileText,
  Scale
} from 'lucide-react';

import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { useFormat } from '../../../i18n/format.js';

function FineDetailModal({ fine, vehicles, drivers, onClose, onAction, onDesignate, onMessage }) {
  const { t, fmt } = useFormat();
  // Devise de l'organisation de l'amende — un seul outil monétaire.
  const { money } = useOrgCurrency();
  const fmtMoney = (v) => (v == null || v === '' ? '-' : money(v, fine));
  const vehicle = vehicles.find((v) => v.id === fine.vehicle_id);
  const designatedDriver = drivers.find((d) => d.id === fine.designated_driver_id);

  // Couleurs seules : les libellés se résolvent au rendu (t), sinon la langue serait figée.
  const statusConfig = {
    received: { color: 'bg-gray-100 text-gray-800' },
    pending_designation: { color: 'bg-yellow-100 text-yellow-800' },
    designated: { color: 'bg-blue-100 text-blue-800' },
    contested: { color: 'bg-purple-100 text-purple-800' },
    paid: { color: 'bg-green-100 text-green-800' },
    cancelled: { color: 'bg-gray-100 text-gray-500' },
  };

  const urgencyConfig = {
    overdue: { color: 'bg-red-500 text-white' },
    critical: { color: 'bg-red-100 text-red-800' },
    warning: { color: 'bg-orange-100 text-orange-800' },
    normal: { color: 'bg-green-100 text-green-800' },
  };

  const statusCode = statusConfig[fine.status] ? fine.status : 'received';
  const status = statusConfig[statusCode];
  const urgency = fine.urgency ? urgencyConfig[fine.urgency] : null;

  const handleAction = async (action) => {
    try {
      await onAction(fine.id, action);
      onClose();
    } catch (error) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: error.message,
        type: 'error',
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div>
              <h2 className="text-lg font-semibold">
                {fine.antai_reference || t('fines.detail.titleFallback', { id: fine.id })}
              </h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                {t(`fines.status.${statusCode}`)}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Urgency Alert */}
          {urgency && fine.status === 'pending_designation' && (
            <div className={`p-3 rounded-lg ${urgency.color}`}>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span className="font-medium">
                  {fine.days_remaining !== null
                    ? fine.days_remaining <= 0
                      ? t('fines.detail.deadlineOverdue', { count: Math.abs(fine.days_remaining) })
                      : t('fines.detail.deadlineRemaining', { count: fine.days_remaining })
                    : t(`fines.urgency.${fine.urgency}`)}
                </span>
              </div>
              {fine.designation_deadline && (
                <div className="text-sm mt-1 opacity-90">
                  {t('fines.detail.deadlineDate', { date: fmt.date(fine.designation_deadline) })}
                </div>
              )}
            </div>
          )}

          {/* Vehicle Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Car className="w-4 h-4" />
              {t('fines.detail.vehicleSection')}
            </h3>
            {vehicle ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">{t('fines.detail.plate')}</div>
                  <div className="font-medium">{vehicle.license_plate}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">{t('fines.detail.vehicle')}</div>
                  <div className="font-medium">{vehicle.make} {vehicle.model}</div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500">{t('fines.detail.vehicleNotFound')}</div>
            )}
          </div>

          {/* Infraction Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {t('fines.detail.infractionDate')}
              </div>
              <div className="font-medium">
                {fine.infraction_time
                  ? t('fines.detail.dateWithTime', { date: fmt.date(fine.infraction_date), time: fine.infraction_time })
                  : fmt.date(fine.infraction_date)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">{t('fines.detail.infractionType')}</div>
              <div className="font-medium">
                {t(`fines.infractionTypes.${fine.infraction_type}`, { defaultValue: fine.infraction_type })}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-sm text-gray-500 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {t('fines.detail.place')}
              </div>
              <div className="font-medium">{fine.location || '-'}</div>
            </div>
          </div>

          {/* Financial Details */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-700 mb-3 flex items-center gap-2">
              <Euro className="w-4 h-4" />
              {t('fines.detail.financialSection')}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-blue-600">{t('fines.detail.amount')}</div>
                <div className="text-xl font-bold text-blue-800">{fmtMoney(fine.fine_amount)}</div>
              </div>
              {fine.reduced_amount && (
                <div>
                  <div className="text-sm text-green-600">{t('fines.detail.reducedAmount')}</div>
                  <div className="text-lg font-medium text-green-700">{fmtMoney(fine.reduced_amount)}</div>
                </div>
              )}
              {fine.increased_amount && (
                <div>
                  <div className="text-sm text-red-600">{t('fines.detail.increasedAmount')}</div>
                  <div className="text-lg font-medium text-red-700">{fmtMoney(fine.increased_amount)}</div>
                </div>
              )}
            </div>
            {fine.points_deducted > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <span className="text-red-600 font-medium">
                  {t('fines.detail.pointsLost', { count: fine.points_deducted })}
                </span>
              </div>
            )}
          </div>

          {/* Driver Designation */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              {t('fines.detail.designationSection')}
            </h3>
            {designatedDriver ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-medium">
                    {designatedDriver.first_name} {designatedDriver.last_name}
                  </div>
                  <div className="text-sm text-gray-500">{designatedDriver.email}</div>
                  {fine.designated_at && (
                    <div className="text-xs text-gray-400">
                      {t('fines.detail.designatedOn', { date: fmt.date(fine.designated_at) })}
                    </div>
                  )}
                </div>
              </div>
            ) : fine.status === 'pending_designation' ? (
              <div className="text-center py-4">
                <p className="text-gray-500 mb-3">{t('fines.detail.noDriver')}</p>
                <button
                  onClick={onDesignate}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 mx-auto"
                >
                  <UserCheck className="w-4 h-4" />
                  {t('fines.detail.designateDriver')}
                </button>
              </div>
            ) : (
              <div className="text-gray-500">{t('fines.detail.notApplicable')}</div>
            )}
          </div>

          {/* Contest Info */}
          {fine.status === 'contested' && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
                <Scale className="w-4 h-4" />
                {t('fines.detail.contestSection')}
              </h3>
              <div className="space-y-2">
                {fine.contest_reason && (
                  <div>
                    <div className="text-sm text-purple-600">{t('fines.detail.contestReason')}</div>
                    <div className="font-medium">{fine.contest_reason}</div>
                  </div>
                )}
                {fine.contest_outcome && (
                  <div>
                    <div className="text-sm text-purple-600">{t('fines.detail.contestOutcome')}</div>
                    <div className="font-medium capitalize">{fine.contest_outcome}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {fine.notes && (
            <div>
              <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('fines.detail.notes')}
              </h3>
              <div className="bg-gray-50 rounded-lg p-3 text-gray-700 whitespace-pre-wrap">
                {fine.notes}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 flex flex-wrap gap-2 justify-end">
          {fine.status === 'received' && (
            <button
              onClick={() => handleAction('process')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('fines.detail.process')}
            </button>
          )}

          {fine.status === 'pending_designation' && (
            <>
              <button
                onClick={onDesignate}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                <UserCheck className="w-4 h-4" />
                {t('fines.detail.designate')}
              </button>
              <button
                onClick={() => handleAction('contest')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {t('fines.detail.contest')}
              </button>
            </>
          )}

          {fine.status === 'designated' && (
            <>
              <button
                onClick={() => handleAction('pay')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                {t('fines.detail.markPaid')}
              </button>
              <button
                onClick={() => handleAction('contest')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {t('fines.detail.contest')}
              </button>
            </>
          )}

          {fine.status === 'contested' && (
            <button
              onClick={() => handleAction('pay')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {t('fines.detail.markPaid')}
            </button>
          )}

          {!['paid', 'cancelled'].includes(fine.status) && (
            <button
              onClick={() => handleAction('cancel')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {t('common.actions.cancel')}
            </button>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            {t('common.actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FineDetailModal;
